import React, { useState } from 'react';
import { ExternalLink, Maximize2, RefreshCw, Loader2 } from 'lucide-react';

interface IframeModuleProps {
  moduleName: string;
  url: string;
  accentColor?: string;
}

const IframeModule: React.FC<IframeModuleProps> = ({ moduleName, url, accentColor = '#C9A84C' }) => {
  const [isLoading, setIsLoading] = useState(true);

  const handleOpenNewTab = () => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleRefresh = () => {
    setIsLoading(true);
    const iframe = document.getElementById(`iframe-${moduleName}`) as HTMLIFrameElement;
    if (iframe) {
      iframe.src = url;
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-70px)]">
      {/* Toolbar */}
      <div 
        className="flex items-center justify-between px-5 py-2.5 bg-navy-card border-b border-navy-border"
        style={{ borderBottomColor: `${accentColor}22` }}
      >
        <div className="flex items-center space-x-3">
          <div 
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: accentColor }}
          />
          <h2 className="text-sm font-semibold text-text-heading tracking-wide uppercase">
            {moduleName}
          </h2>
          {isLoading && (
            <Loader2 size={14} className="text-text-muted animate-spin" />
          )}
        </div>

        <div className="flex items-center space-x-1">
          <button
            onClick={handleRefresh}
            className="p-1.5 rounded-md text-text-muted hover:text-text-heading hover:bg-navy-border/50 transition-all duration-150"
            aria-label={`Refresh ${moduleName}`}
            title="Refresh"
          >
            <RefreshCw size={14} />
          </button>
          <button
            onClick={handleOpenNewTab}
            className="flex items-center space-x-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-text-muted hover:text-gold-primary hover:bg-gold-primary/5 transition-all duration-150"
            aria-label={`Open ${moduleName} in new tab`}
          >
            <span>Open in new tab</span>
            <ExternalLink size={12} />
          </button>
        </div>
      </div>

      {/* Iframe Container */}
      <div className="flex-1 relative bg-navy-primary">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-navy-primary z-10">
            <div className="text-center">
              <div 
                className="w-10 h-10 border-3 border-t-transparent rounded-full animate-spin mx-auto mb-3"
                style={{ borderColor: `${accentColor}44`, borderTopColor: 'transparent' }}
              />
              <p className="text-sm text-text-muted">Loading {moduleName}…</p>
            </div>
          </div>
        )}
        <iframe
          id={`iframe-${moduleName}`}
          src={url}
          title={`${moduleName} - AFN Nexus`}
          className="w-full h-full border-0"
          onLoad={() => setIsLoading(false)}
          allow="clipboard-read; clipboard-write; fullscreen"
          sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-downloads allow-modals"
        />
      </div>
    </div>
  );
};

export default IframeModule;
